import { createOrder, listOrders, deleteOrder, updateOrder, listUserOrders, createOrderForUser } from '../lib/db/orders';

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export class OrdersAPI {
  static async list(): Promise<ApiResponse> {
    const result = await listOrders();
    if (!result.success) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data };
  }

  static async listByUser(clerkId: string): Promise<ApiResponse> {
    const result = await listUserOrders(clerkId);
    if (!result.success) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data };
  }

  static async delete(id: string): Promise<ApiResponse> {
    const result = await deleteOrder(id);
    if (!result.success) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data };
  }

  static async update(id: string, data: any): Promise<ApiResponse> {
    const result = await updateOrder(id, data);
    if (!result.success) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data };
  }

  static async create(clerkId: string, itemId: string, emailData?: {
    userEmail: string;
    userName: string;
    planName: string;
    planPrice: string;
    planCurrency: string;
    planPeriod: string;
  }, domain?: string, itemType: 'hosting' | 'complement' = 'hosting', quantity: number = 1): Promise<ApiResponse> {
    const result = await createOrder(clerkId, itemId, domain, itemType, quantity);

    if (result.success && emailData) {
      fetch('/api/send-order-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData)
      }).catch(err => console.error('Error sending order email:', err));
    }

    if (!result.success) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data };
  }

  static async sendStatusEmail(data: {
    userEmail: string;
    userName: string;
    planName: string;
    planPrice: string;
    planCurrency: string;
    planPeriod: string;
    status?: string;
    renewalDate?: string;
    type: 'STATUS_CHANGE' | 'DELETED' | 'DATE_CHANGE';
  }): Promise<void> {
    fetch('/api/send-status-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).catch(err => console.error('Error sending status email:', err));
  }

  static async sendCancelEmail(data: {
    userEmail: string;
    userName: string;
    planName: string;
    planPrice: string;
    planCurrency: string;
    planPeriod: string;
  }): Promise<void> {
    fetch('/api/send-cancel-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).catch(err => console.error('Error sending cancel email:', err));
  }

  static async createAsAdmin(data: {
    userId: string;
    hostingId?: string;
    complementId?: string;
    domain?: string;
    status?: string;
    createdAt?: string;
    quantity?: number;
    lastPaymentMethod?: string;
  }): Promise<ApiResponse> {
    const date = data.createdAt ? new Date(data.createdAt + 'T12:00:00') : undefined
    const result = await createOrderForUser(
      data.userId,
      data.hostingId,
      data.domain,
      data.status,
      date,
      data.complementId,
      data.quantity,
      data.lastPaymentMethod
    );

    if (!result.success) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data };
  }

  static async getCpanelCredentials(id: string, token: string): Promise<ApiResponse> {
    try {
      const res = await fetch(`/api/hosting-credentials?id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data.error || 'Failed to fetch credentials' };
      }
      return { success: true, data: data.data };
    } catch (err: any) {
      console.error('Error fetching cPanel credentials:', err);
      return { success: false, error: err.message };
    }
  }
}
